import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { AlertTriangle, CheckCircle2, Copy, KeyRound, LockKeyhole, RefreshCw, Search, ShieldCheck, UserCog } from 'lucide-react';
import { useAuth } from '@/core/hooks';

interface AccessRestrictions {
  branchIds: string[];
  clientIds: string[];
  carerIds: string[];
}

interface TrustedDeviceSummary {
  id: string;
  createdAt: string;
  lastUsedAt: string;
  expiresAt: string;
  ip: string;
  userAgent: string;
}

interface SupportUser {
  id: string;
  organizationId: string;
  name: string;
  email: string;
  emailNormalized: string;
  phone: string;
  roles: string[];
  roleLabels: string[];
  permissions: string[];
  directPermissions: string[];
  accessRestrictions: AccessRestrictions;
  disabled: boolean;
  passwordUpdatedAt: string;
  activeSessionCount: number;
  totalSessionCount: number;
  lastSessionAt: string;
  trustedDeviceCount: number;
  trustedDevices: TrustedDeviceSummary[];
  credentialNotice: string;
}

interface SupportPayload {
  users: SupportUser[];
  passwordPolicy: {
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumber: boolean;
    requireSpecial: boolean;
  };
}

function formatDate(value?: string) {
  if (!value) return 'Not recorded';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function generateTemporaryPassword() {
  const random = Math.random().toString(36).slice(2, 10);
  const suffix = Math.floor(1000 + Math.random() * 9000);
  return `Fuchsia@${new Date().getFullYear()}-${random}-${suffix}`;
}

async function copyToClipboard(value: string, label: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`${label} copied.`);
  } catch {
    toast.error(`Could not copy ${label.toLowerCase()}.`);
  }
}

export const SuperAdminUserCredentials: React.FC = () => {
  const { token } = useAuth();
  const [payload, setPayload] = useState<SupportPayload | null>(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [query, setQuery] = useState('');
  const [temporaryPassword, setTemporaryPassword] = useState(generateTemporaryPassword);
  const [revokeSessions, setRevokeSessions] = useState(true);
  const [clearTrustedDevices, setClearTrustedDevices] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const users = payload?.users || [];
  const filteredUsers = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return users;
    return users.filter((user) =>
      [user.name, user.email, user.id, ...user.roleLabels, ...user.roles]
        .join(' ')
        .toLowerCase()
        .includes(value)
    );
  }, [query, users]);

  const selectedUser = users.find((user) => user.id === selectedUserId) || filteredUsers[0] || users[0] || null;

  const loadUsers = async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/superadmin-users', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Could not load user credentials panel');
      setPayload(data.data);
      setSelectedUserId((current) => data.data.users.some((user: SupportUser) => user.id === current) ? current : data.data.users[0]?.id || '');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not load user credentials panel');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, [token]);

  const resetPassword = async () => {
    if (!token || !selectedUser) return;
    const confirmed = window.confirm(
      `Set a temporary password for ${selectedUser.name}? The action is audited and old sessions can be revoked.`
    );
    if (!confirmed) return;

    setIsSaving(true);
    try {
      const response = await fetch('/api/auth/superadmin-users/password', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: selectedUser.id,
          newPassword: temporaryPassword,
          revokeSessions,
          clearTrustedDevices,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'Could not set temporary password');
      setPayload((current) => current ? { ...current, users: data.data.users } : current);
      toast.success('Temporary password saved for this user.');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not set temporary password');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center">
        <RefreshCw className="h-6 w-6 animate-spin text-[#6c3a80]" />
      </div>
    );
  }

  if (!selectedUser || !payload) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-10">
        <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-red-800">
          <h1 className="text-xl font-bold">SuperAdmin panel unavailable</h1>
          <p className="mt-2 text-sm">Only SuperAdmin can open the complete user support panel.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <header className="flex flex-col gap-4 border-b border-slate-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-[#6c3a80]">
            <ShieldCheck className="h-4 w-4" />
            SuperAdmin only
          </div>
          <h1 className="mt-2 text-3xl font-bold text-slate-950">User Credentials & Account Support</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            View complete account support data, check session status and set a temporary password when a user cannot access the application.
          </p>
        </div>
        <button
          type="button"
          onClick={loadUsers}
          className="inline-flex h-10 items-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
        >
          <RefreshCw className="h-4 w-4" />
          Refresh
        </button>
      </header>

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm leading-6 text-amber-900">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
          <p>
            Existing passwords are not displayed because the system stores them as secure hashes. For support, SuperAdmin can set a temporary password, log in to verify the issue, then ask the user to change it.
          </p>
        </div>
      </div>

      <section className="grid gap-5 lg:grid-cols-[340px_1fr]">
        <aside className="space-y-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <label className="relative block">
              <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
              <input
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search user, email, role..."
                className="h-10 w-full rounded-md border border-slate-300 pl-9 pr-3 text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
              />
            </label>
            <div className="mt-4 max-h-[640px] space-y-2 overflow-y-auto">
              {filteredUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  onClick={() => setSelectedUserId(user.id)}
                  className={`w-full rounded-md border px-3 py-3 text-left text-sm transition ${
                    selectedUser.id === user.id
                      ? 'border-[#6c3a80] bg-[#fbf7fd] text-[#5e3474]'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="font-semibold">{user.name}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${user.disabled ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                      {user.disabled ? 'Disabled' : 'Active'}
                    </span>
                  </span>
                  <span className="mt-1 block truncate text-xs text-slate-500">{user.email}</span>
                  <span className="mt-2 block truncate text-xs font-medium text-slate-500">{user.roleLabels.join(', ') || 'No role'}</span>
                </button>
              ))}
            </div>
          </div>
        </aside>

        <main className="space-y-5">
          <section className="grid gap-4 xl:grid-cols-3">
            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-slate-950">{selectedUser.name}</h2>
                  <p className="mt-1 text-sm text-slate-500">{selectedUser.email}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedUser.roleLabels.map((role) => (
                    <span key={role} className="rounded-full bg-[#efe4f4] px-3 py-1 text-xs font-bold text-[#5e3474]">
                      {role}
                    </span>
                  ))}
                </div>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-2">
                {[
                  ['User ID', selectedUser.id],
                  ['Organisation ID', selectedUser.organizationId || 'Not recorded'],
                  ['Email login', selectedUser.email],
                  ['Phone', selectedUser.phone || 'Not recorded'],
                  ['Password updated', formatDate(selectedUser.passwordUpdatedAt)],
                  ['Last session activity', formatDate(selectedUser.lastSessionAt)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md bg-slate-50 p-3">
                    <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</p>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <p className="break-all text-sm font-semibold text-slate-950">{value}</p>
                      {['User ID', 'Email login'].includes(label) && (
                        <button type="button" onClick={() => copyToClipboard(value, label)} className="rounded p-1 text-slate-500 hover:bg-white hover:text-[#6c3a80]">
                          <Copy className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <LockKeyhole className="h-5 w-5 text-[#6c3a80]" />
                <h3 className="font-bold text-slate-950">Security status</h3>
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <div className="flex items-center justify-between rounded-md bg-slate-50 p-3">
                  <span>Active sessions</span>
                  <span className="font-bold">{selectedUser.activeSessionCount}</span>
                </div>
                <div className="flex items-center justify-between rounded-md bg-slate-50 p-3">
                  <span>Saved browser sessions</span>
                  <span className="font-bold">{selectedUser.trustedDeviceCount}</span>
                </div>
              </div>
            </div>
          </section>

          <section className="grid gap-5 xl:grid-cols-[1fr_420px]">
            <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <KeyRound className="h-5 w-5 text-[#6c3a80]" />
                <h3 className="font-bold text-slate-950">Temporary password support</h3>
              </div>
              <p className="mt-2 text-sm leading-6 text-slate-600">
                Use this only when a user cannot access their account. The action is recorded in the audit log.
              </p>

              <div className="mt-4 grid gap-3 sm:grid-cols-[1fr_auto_auto]">
                <input
                  value={temporaryPassword}
                  onChange={(event) => setTemporaryPassword(event.target.value)}
                  className="h-11 rounded-md border border-slate-300 px-3 font-mono text-sm outline-none focus:border-[#6c3a80] focus:ring-2 focus:ring-[#6c3a80]/20"
                />
                <button
                  type="button"
                  onClick={() => setTemporaryPassword(generateTemporaryPassword())}
                  className="inline-flex h-11 items-center justify-center rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Generate
                </button>
                <button
                  type="button"
                  onClick={() => copyToClipboard(temporaryPassword, 'Temporary password')}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  <Copy className="h-4 w-4" />
                  Copy
                </button>
              </div>

              <div className="mt-4 grid gap-3 text-sm text-slate-700 md:grid-cols-2">
                <label className="flex items-start gap-2 rounded-md bg-slate-50 p-3">
                  <input type="checkbox" checked={revokeSessions} onChange={(event) => setRevokeSessions(event.target.checked)} className="mt-1 h-4 w-4 accent-[#6c3a80]" />
                  <span>Revoke old sessions</span>
                </label>
                <label className="flex items-start gap-2 rounded-md bg-slate-50 p-3">
                  <input type="checkbox" checked={clearTrustedDevices} onChange={(event) => setClearTrustedDevices(event.target.checked)} className="mt-1 h-4 w-4 accent-[#6c3a80]" />
                  <span>Clear saved device sessions</span>
                </label>
              </div>

              <button
                type="button"
                onClick={resetPassword}
                disabled={isSaving}
                className="mt-4 inline-flex h-11 items-center gap-2 rounded-md bg-[#6c3a80] px-5 text-sm font-bold text-white hover:bg-[#5e3474] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Save temporary password
              </button>

              <p className="mt-3 text-xs leading-5 text-slate-500">
                Password policy: minimum {payload.passwordPolicy.minLength} characters, uppercase, lowercase, number and special character according to server settings.
              </p>
            </article>

            <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex items-center gap-2">
                <UserCog className="h-5 w-5 text-[#6c3a80]" />
                <h3 className="font-bold text-slate-950">Access restrictions</h3>
              </div>
              <div className="mt-4 space-y-3 text-sm">
                <div className="rounded-md bg-slate-50 p-3">
                  <p className="font-bold text-slate-900">Branches</p>
                  <p className="mt-1 text-slate-600">{selectedUser.accessRestrictions.branchIds.length ? selectedUser.accessRestrictions.branchIds.join(', ') : 'All branches'}</p>
                </div>
                <div className="rounded-md bg-slate-50 p-3">
                  <p className="font-bold text-slate-900">Clients</p>
                  <p className="mt-1 text-slate-600">{selectedUser.accessRestrictions.clientIds.length ? selectedUser.accessRestrictions.clientIds.join(', ') : 'All clients'}</p>
                </div>
                <div className="rounded-md bg-slate-50 p-3">
                  <p className="font-bold text-slate-900">Carers</p>
                  <p className="mt-1 text-slate-600">{selectedUser.accessRestrictions.carerIds.length ? selectedUser.accessRestrictions.carerIds.join(', ') : 'All carers'}</p>
                </div>
              </div>
            </article>
          </section>

          <section className="grid gap-5 xl:grid-cols-2">
            <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="font-bold text-slate-950">Permissions</h3>
              <p className="mt-2 max-h-52 overflow-y-auto rounded-md bg-slate-50 p-3 text-sm leading-6 text-slate-700">
                {selectedUser.permissions.includes('*') ? 'Full platform access.' : selectedUser.permissions.join(', ') || 'No explicit permissions recorded.'}
              </p>
            </article>

            <article className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="font-bold text-slate-950">Saved browser sessions</h3>
              <div className="mt-3 max-h-52 space-y-2 overflow-y-auto">
                {selectedUser.trustedDevices.length ? selectedUser.trustedDevices.map((device) => (
                  <div key={device.id} className="rounded-md bg-slate-50 p-3 text-xs leading-5 text-slate-600">
                    <p className="font-bold text-slate-900">{device.ip || 'Unknown IP'}</p>
                    <p>Last used: {formatDate(device.lastUsedAt)}</p>
                    <p>Expires: {formatDate(device.expiresAt)}</p>
                    <p className="truncate">{device.userAgent || 'Unknown device'}</p>
                  </div>
                )) : (
                  <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">No saved browser sessions recorded.</p>
                )}
              </div>
            </article>
          </section>
        </main>
      </section>
    </div>
  );
};
